import { useMutation } from '@apollo/client';
import { AdminButton } from '@website/components/admin/AdminButton';
import ConfirmDialog from '@website/components/utilities/ConfirmDialog';
import Truncate from '@website/components/utilities/Truncate';
import {
  RemoveReplyFromReviewDocument,
  Review,
  TripsPageQuery,
} from '@website/types/graphqlOperations';
import { useCallback, useState } from 'react';

export function useRemoveReplyFromReview() {
  const [removeReplyMutation, { loading }] = useMutation(
    RemoveReplyFromReviewDocument,
  );
  return { removeReplyMutation, loading };
}

type Props = {
  review: Pick<Review, 'id' | 'reply' | 'body'> & {
    user: TripsPageQuery['tripByURL']['reviews']['items'][number]['user'];
  };
};

export function RemoveReplyFromReview({ review }: Props) {
  const [show, setShow] = useState(false);

  const { removeReplyMutation, loading } = useRemoveReplyFromReview();

  const removeReply = useCallback(
    async (id: string) => {
      await removeReplyMutation({
        variables: {
          id,
        },
      });
      setShow(false);
    },
    [removeReplyMutation],
  );

  if (!review.reply) return null;

  return (
    <>
      <ConfirmDialog
        body={
          <Truncate lines={3} enableExpand className="text-muted text-sm">
            {review.reply.content}
          </Truncate>
        }
        title={`Are you sure you want to remove the reply from this review by ${review.user.givenName}?`}
        visible={show}
        setVisible={setShow}
        onConfirmFn={() => removeReply(review.id)}
      />

      <AdminButton
        className="mx-1"
        disabled={loading}
        variant="danger"
        onClick={() => {
          setShow(true);
        }}
      >
        {loading ? 'Removing reply...' : 'Remove reply'}
      </AdminButton>
    </>
  );
}
